import * as React from "react";
import moment from "moment";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import API from "../../Api/api";
import StatCard from "../../ui/StatCard";
import EmptyState from "../../ui/EmptyState";
import {
  emptyReportFilter,
  formatDate,
  formatMoney,
  money,
  premiumRow,
  sumBy,
} from "../../utils/premium";

const moneyCol = (field, headerName) => ({
  field,
  headerName,
  type: "number",
  minWidth: 140,
  valueFormatter: ({ value }) => formatMoney(value),
});

const columns = [
  { field: "policy_number", headerName: "Policy number", minWidth: 160 },
  { field: "description", headerName: "Description", minWidth: 200 },
  { field: "institution", headerName: "Institution", minWidth: 160 },
  { field: "policy_type", headerName: "Policy type", minWidth: 140 },
  { field: "purchase_date", headerName: "Start date", minWidth: 115, valueFormatter: ({ value }) => formatDate(value) },
  { field: "maturity_date", headerName: "Maturity date", minWidth: 120, valueFormatter: ({ value }) => formatDate(value) },
  { field: "tenure", headerName: "Tenure", type: "number", minWidth: 90 },
  { field: "days_earned", headerName: "Days earned", type: "number", minWidth: 110 },
  { field: "days_unearned", headerName: "Days unearned", type: "number", minWidth: 120 },
  moneyCol("cost", "Premium"),
  moneyCol("cumulative_premium", "Cumulative premium"),
  moneyCol("premium_uncharged", "Premium uncharged"),
  moneyCol("premium_charged", "Premium charged"),
  moneyCol("daily_premium", "Daily premium"),
  {
    field: "status",
    headerName: "Status",
    minWidth: 130,
    renderCell: ({ value }) => (
      <Chip
        size="small"
        variant="outlined"
        label={value === "investment was not available then" ? "Not yet bought" : value}
        color={value === "Active" ? "success" : value === "Exhausted" ? "default" : "warning"}
      />
    ),
  },
];

function DateRange({ title, from, to, onFrom, onTo }) {
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Stack direction="row" gap={1}>
        <TextField label="From" type="date" fullWidth value={from} onChange={(e) => onFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField label="To" type="date" fullWidth value={to} onChange={(e) => onTo(e.target.value)} InputLabelProps={{ shrink: true }} />
      </Stack>
    </Box>
  );
}

export default function InsuranceReport() {
  const [view, setView] = React.useState("");
  const [filterData, setFilterData] = React.useState(emptyReportFilter);
  const allApprovedInsurance = useSelector((state) => state.approved);
  const loadedApproved = useSelector((state) => state.loaded.approved);
  const [data, setData] = React.useState(allApprovedInsurance);
  const [allInsurance, setAllInsurance] = React.useState(allApprovedInsurance); // Separate the original data array
  const [generated, setGenerated] = React.useState(false);
  const [fetching, setFetching] = React.useState(false);
  const setField = (key) => (value) => setFilterData({ ...filterData, [key]: value });

  // Opening this page straight away: the approved list arrives after the first draw
  React.useEffect(() => {
    if (generated) return;
    setAllInsurance(allApprovedInsurance);
    setData(allApprovedInsurance.filter((d) => moment(d.maturity_date) > moment()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allApprovedInsurance]);

  const rows = filterData.premium_charged_start
    ? data
        .filter((d) => moment(d.maturity_date) >= moment(filterData.premium_charged_start))
        .map((d) => premiumRow(d, filterData))
    : data.map((d) => premiumRow(d, filterData));

  const currentDate = new Date();
  const filterDate = filterData.premium_charged_end
    ? moment(filterData.premium_charged_end)
    : moment(currentDate);
  const filterDate2 = filterData.premium_charged_start
    ? moment(filterData.premium_charged_start)
    : moment();
  React.useEffect(() => {
    if (view === "all insurance") {
      const allAtiveInsurance = allInsurance.filter(
        (insurance) => moment(insurance.maturity_date) >= filterDate2
      );
      setData(allAtiveInsurance);
    } else if (view === "active insurance") {
      const activeInsurance = allInsurance.filter(
        (insurance) => moment(insurance.maturity_date) >= filterDate
      );
      setData(activeInsurance);
    } else if (view === "exhausted insurance") {
      const exhaustedInsurance = allInsurance.filter(
        (insurance) => moment(insurance.maturity_date) < moment()
      );
      setData(exhaustedInsurance);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, allInsurance]);

  React.useEffect(() => {
    if (!view && !filterData.premium_charged_start) {
      const activeInsurance = data.filter((data) => moment(data.maturity_date) > moment());
      setData(activeInsurance);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, filterData.premium_charged_start]);

  const purchase_date_start = filterData.purchased_start
    ? `&purchase_date[gte]=${filterData.purchased_start}`
    : "";
  const purchase_date_end = filterData.purchased_end
    ? `&purchase_date[lte]=${filterData.purchased_end}`
    : "";
  const expiration_start = filterData.maturity_start
    ? `&maturity_date[gte]=${filterData.maturity_start}`
    : "";
  const expiration_end = filterData.maturity_end
    ? `&maturity_date[lte]=${filterData.maturity_end}`
    : "";

  const query = `/api/v1/company-insurance?approved=true${purchase_date_end}${purchase_date_start}${expiration_start}${expiration_end}`;
  const fetchInvestment = async () => {
    if (
      !purchase_date_end &&
      !purchase_date_start &&
      !expiration_end &&
      !expiration_start &&
      !filterData.premium_charged_end &&
      !filterData.premium_charged_start
    ) {
      toast.error("Pick at least one date before generating the report");
      return;
    }
    const fetchingId = toast.loading("Generating report...");
    setFetching(true);
    try {
      const res = await API.get(query);
      setData(res.data);
      setAllInsurance(res.data);
      setGenerated(true);
      toast.success(`Report ready: ${res.data.length} policies`, { id: fetchingId });
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message, { id: fetchingId });
    } finally {
      setFetching(false);
    }
  };
  function clearFilter() {
    setData(allApprovedInsurance);
    setAllInsurance(allApprovedInsurance);
    setGenerated(false);
    setView("");
    setFilterData(emptyReportFilter);
  }

  const hasFilter = Object.values(filterData).some(Boolean) || view;
  const totals = [
    { label: "Total premium", value: money.format(sumBy(rows, "cost")) },
    { label: "Premium charged", value: money.format(sumBy(rows, "premium_charged")) },
    { label: "Premium uncharged", value: money.format(sumBy(rows, "premium_uncharged")) },
    { label: "Cumulative premium", value: money.format(sumBy(rows, "cumulative_premium")) },
  ];

  return (
    <Stack gap={2.5}>
      <Card sx={{ p: { xs: 2, md: 2.5 } }}>
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={4}>
            <DateRange
              title="Premium charged period"
              from={filterData.premium_charged_start}
              to={filterData.premium_charged_end}
              onFrom={setField("premium_charged_start")}
              onTo={setField("premium_charged_end")}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <DateRange
              title="Bought between"
              from={filterData.purchased_start}
              to={filterData.purchased_end}
              onFrom={setField("purchased_start")}
              onTo={setField("purchased_end")}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <DateRange
              title="Matures between"
              from={filterData.maturity_start}
              to={filterData.maturity_end}
              onFrom={setField("maturity_start")}
              onTo={setField("maturity_end")}
            />
          </Grid>
        </Grid>
        <Divider sx={{ my: 2 }} />
        <Stack direction={{ xs: "column", sm: "row" }} gap={1.5} alignItems={{ sm: "center" }}>
          <TextField
            select
            label="Show"
            value={view}
            onChange={(e) => setView(e.target.value)}
            sx={{ minWidth: 220 }}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="">Default (active today)</MenuItem>
            <MenuItem value="active insurance">Active insurance</MenuItem>
            <MenuItem value="exhausted insurance">Exhausted insurance</MenuItem>
            <MenuItem value="all insurance">All insurance</MenuItem>
          </TextField>
          <Box sx={{ flex: 1 }} />
          {hasFilter && (
            <Button startIcon={<FilterAltOffOutlinedIcon />} onClick={clearFilter}>
              Clear filters
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<PlayArrowRoundedIcon />}
            onClick={fetchInvestment}
            disabled={fetching}
          >
            {fetching ? "Generating..." : "Generate report"}
          </Button>
        </Stack>
      </Card>

      <Grid container spacing={2}>
        {totals.map((t) => (
          <Grid item xs={12} sm={6} lg={3} key={t.label}>
            <StatCard label={t.label} value={t.value} hint={`${rows.length} policies`} loading={!loadedApproved} />
          </Grid>
        ))}
      </Grid>

      <Box sx={{ height: "calc(100vh - 250px)", minHeight: 460 }}>
        <DataGrid
          columns={columns}
          rows={rows}
          loading={!loadedApproved || fetching}
          density="compact"
          disableRowSelectionOnClick
          slots={{
            toolbar: GridToolbar,
            noRowsOverlay: () => (
              <EmptyState title="No policies in this report" text="Change the dates or the Show option." />
            ),
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              csvOptions: { fileName: `insurance-report-${moment().format("YYYY-MM-DD")}` },
              printOptions: { hideFooter: true, hideToolbar: true },
            },
          }}
          initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
          pageSizeOptions={[25, 50, 100]}
        />
      </Box>
    </Stack>
  );
}
